import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BoltIcon, 
  SunIcon,
  ChartBarIcon,
  ArrowPathIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import PowerIrradianceChart from '../components/charts/PowerIrradianceChart';
import GenerationTrendChart from '../components/charts/GenerationTrendChart';
import PlantSelector from '../components/Dashboard/PlantSelector';
import { plantApi, PlantPowerIrradianceResponse, PlantDailyExportResponse, PlantSummary } from '../services/plantApi';

const PlantDashboard: React.FC = () => {
  const { plantSlug } = useParams<{ plantSlug: string }>();
  const navigate = useNavigate();
  
  const [powerIrradianceData, setPowerIrradianceData] = useState<PlantPowerIrradianceResponse | null>(null);
  const [dailyExportData, setDailyExportData] = useState<PlantDailyExportResponse | null>(null);
  const [summary, setSummary] = useState<PlantSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');

  const currentPlantSlug = plantSlug || 'rayzen-solar-plant';

  useEffect(() => {
    fetchDashboardData();
  }, [currentPlantSlug, timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const hours = timeRange === '24h' ? 24 : timeRange === '48h' ? 48 : 72;
      
      const [powerIrradiance, dailyExport, summaryData] = await Promise.all([
        plantApi.getPlantPowerIrradiance(currentPlantSlug, hours),
        plantApi.getPlantDailyExport(currentPlantSlug, 7),
        plantApi.getPlantSummary(currentPlantSlug)
      ]);
      
      setPowerIrradianceData(powerIrradiance);
      setDailyExportData(dailyExport);
      setSummary(summaryData);
      
    } catch (error) {
      console.error('Error fetching plant dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlantChange = (newPlantSlug: string) => {
    navigate(`/plant/${newPlantSlug}`);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Plant Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {summary?.plant_name || 'Loading...'} • {summary?.plant_location || ''}
            </p>
          </div>
          
          <PlantSelector
            selectedPlant={currentPlantSlug}
            onPlantChange={handlePlantChange}
          />
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="48h">Last 48 Hours</option>
            <option value="72h">Last 72 Hours</option>
          </select>
          
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <BuildingOfficeIcon className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{summary?.plant_name}</h2>
              <p className="text-blue-100">{summary?.plant_location}</p>
              <p className="text-sm text-blue-200">Capacity: {summary?.plant_capacity_kw ? summary.plant_capacity_kw.toFixed(0) : '0'} kW</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold">
              {((summary?.performance_ratio || 0) * 100).toFixed(1)}%
            </div>
            <div className="text-blue-200">Performance Ratio</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <BoltIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.current_power_kw ? summary.current_power_kw.toFixed(1) : '0.0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kW</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Current Power</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Live Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <SunIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.current_irradiance_wm2 ? summary.current_irradiance_wm2.toFixed(0) : '0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">W/m²</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Solar Irradiance</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Current Conditions</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {summary?.today_energy_kwh ? summary.today_energy_kwh.toFixed(0) : '0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kWh</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Today's Energy</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {((powerIrradianceData?.correlation_coefficient || 0) * 100).toFixed(1)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">%</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Correlation</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Power vs Irradiance</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <PowerIrradianceChart
            data={powerIrradianceData?.data || []}
            correlation={powerIrradianceData?.correlation_coefficient || 0}
            loading={loading}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <GenerationTrendChart
            data={dailyExportData?.data || []}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default PlantDashboard;
